import { authFetch } from '../auth/auth-fetch.js';
import type {
  BulkMediaBody,
  BulkMediaResult,
  CreateVocabCategoryBody,
  CreateVocabTagBody,
  HotelMedia,
  MediaApiError,
  MediaComplianceFields,
  MediaFilter,
  MediaListPage,
  MediaPatchBody,
  PatchVocabCategoryBody,
  PatchVocabTagBody,
  PatchVocabTagResult,
  Vocab,
  VocabCategory,
  VocabTag,
} from './types.js';

interface MediaErrorBody {
  code?: string;
  title?: string;
  blockers?: string[];
  existingId?: string;
  unknownTags?: string[];
  mediaId?: string;
  postCount?: number;
}

/**
 * Media-specific counterpart to `features/hotels/api.ts`'s `jsonOrThrow`.
 *
 * The plain `jsonOrThrow` throws a bare `Error` with a generic message and
 * discards the response body — for this feature that is not good enough.
 * The API answers structured failures the interface must tell apart and
 * explain to a marketing colleague: `duplicate_image` names the existing
 * image, `unknown_tag` names the rejected tags, `media_not_attachable`
 * carries the blocker codes, `tag_merge_confirmation_required` asks for
 * confirmation, `compliance_fields_invalid` flags a malformed upload. This
 * helper carries `code` (and, when present, `blockers`/`existingId`/
 * `unknownTags`/`mediaId`) onto the thrown `Error` so callers can branch on
 * them instead of parsing a message string. Every function in this module
 * uses it — never the generic `jsonOrThrow`.
 */
async function mediaJson<T>(res: Response, fallback: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const body = (await res.json().catch(() => ({}))) as MediaErrorBody;
  const err = new Error(body.title ?? fallback) as MediaApiError;
  err.code = body.code;
  err.blockers = body.blockers;
  err.existingId = body.existingId;
  err.unknownTags = body.unknownTags;
  err.mediaId = body.mediaId;
  err.postCount = body.postCount;
  throw err;
}

/** `?tags=a&tags=b` — the API expects one repeated query parameter per tag. */
function buildMediaQuery(filter: MediaFilter, cursor?: string): string {
  const params = new URLSearchParams();
  for (const tag of filter.tags ?? []) params.append('tags', tag);
  if (filter.orientation) params.set('orientation', filter.orientation);
  if (filter.complianceStatus) params.set('complianceStatus', filter.complianceStatus);
  if (filter.aiInvolvement) params.set('aiInvolvement', filter.aiInvolvement);
  if (filter.q) params.set('q', filter.q);
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listMedia(
  hotelId: string,
  filter: MediaFilter = {},
  cursor?: string,
): Promise<MediaListPage> {
  const res = await authFetch(`/hotels/${hotelId}/media${buildMediaQuery(filter, cursor)}`);
  return mediaJson<MediaListPage>(res, 'failed to list media');
}

/**
 * Appends the compliance fields as individual multipart fields, matching how
 * `HotelMediaController.upload` reads them (`data.fields[name]`). A `null`
 * value is omitted rather than sent as the string `"null"` — the API's
 * `.default(null)` treats an absent field the same as an explicit null.
 */
function appendComplianceFields(form: FormData, compliance: MediaComplianceFields): void {
  form.append('rightsBasis', compliance.rightsBasis);
  form.append('aiInvolvement', compliance.aiInvolvement);
  if (compliance.rightsConfirmedAt !== null) {
    form.append('rightsConfirmedAt', compliance.rightsConfirmedAt);
  }
  if (compliance.rightsConfirmedRef !== null) {
    form.append('rightsConfirmedRef', compliance.rightsConfirmedRef);
  }
  if (compliance.aiChangeNote !== null) form.append('aiChangeNote', compliance.aiChangeNote);
  if (compliance.peopleRecognizable !== null) {
    form.append('peopleRecognizable', String(compliance.peopleRecognizable));
  }
  if (compliance.peopleConsent !== null) form.append('peopleConsent', compliance.peopleConsent);
  if (compliance.stockPlatform !== null) form.append('stockPlatform', compliance.stockPlatform);
  if (compliance.stockTerritory !== null) {
    form.append('stockTerritory', compliance.stockTerritory);
  }
  if (compliance.licenseExpiresAt !== null) {
    form.append('licenseExpiresAt', compliance.licenseExpiresAt);
  }
}

export async function uploadMedia(
  hotelId: string,
  file: File,
  args: { tags: string[]; caption: string | null; compliance: MediaComplianceFields },
): Promise<HotelMedia> {
  const form = new FormData();
  form.append('file', file);
  for (const t of args.tags) form.append('tags', t);
  if (args.caption) form.append('caption', args.caption);
  appendComplianceFields(form, args.compliance);
  const res = await authFetch(`/hotels/${hotelId}/media`, { method: 'POST', body: form });
  return mediaJson<HotelMedia>(res, 'failed to upload media');
}

export async function patchMedia(
  hotelId: string,
  id: string,
  patch: MediaPatchBody,
): Promise<HotelMedia> {
  const res = await authFetch(`/hotels/${hotelId}/media/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return mediaJson<HotelMedia>(res, 'failed to update media');
}

/**
 * Two-step delete. Without `detachFromPosts` a picture a post still uses is
 * refused with `media_in_use` (and `postCount` on the error); the caller
 * shows that count, asks again, and retries with `detachFromPosts: true`,
 * which takes the picture off those posts first.
 */
export async function deleteMedia(
  hotelId: string,
  id: string,
  opts: { detachFromPosts?: boolean } = {},
): Promise<void> {
  const suffix = opts.detachFromPosts ? '?detach=true' : '';
  const res = await authFetch(`/hotels/${hotelId}/media/${id}${suffix}`, { method: 'DELETE' });
  await mediaJson<{ id: string }>(res, 'failed to delete media');
}

export async function bulkMedia(hotelId: string, body: BulkMediaBody): Promise<BulkMediaResult> {
  const res = await authFetch(`/hotels/${hotelId}/media/bulk`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return mediaJson<BulkMediaResult>(res, 'failed to run bulk media operation');
}

/**
 * `GET :id/download` answers JSON `{ url, filename }` — a pre-signed storage
 * URL plus the human filename it carries via content-disposition (task 15;
 * changed from a 302 redirect in fix round 1 of task 27). The endpoint
 * itself sits behind the same `JwtAuthGuard` as every other route here, so
 * it needs the Bearer token `authFetch` attaches — a bare `<a href>` to it
 * would get a 401, since a plain browser navigation carries no such header.
 * The URL this returns is different: it is pre-signed by the storage layer,
 * carries its own authorisation, and is safe to open directly (new tab,
 * `window.open`, or an `<a href>` a caller builds AFTER calling this).
 */
export async function downloadMedia(
  hotelId: string,
  id: string,
): Promise<{ url: string; filename: string }> {
  const res = await authFetch(`/hotels/${hotelId}/media/${id}/download`);
  return mediaJson<{ url: string; filename: string }>(res, 'failed to get download link');
}

/**
 * The one React Query key for the org-wide tag/category dictionary. Every
 * reader (upload panel, post-editor drawer, a future admin screen) MUST go
 * through this constant — mirrors `HOTELS_QUERY_KEY` in `features/hotels/api.ts`.
 * The dictionary is editable elsewhere in the app (admin tag/category CRUD),
 * so callers that mutate it invalidate this exact key to make the new tag
 * show up everywhere else without a page reload.
 */
export const MEDIA_VOCAB_QUERY_KEY = ['media-vocab'] as const;

/**
 * Key for the usage-counted variant (`listVocab(true)` → `?usage=1`). It
 * MUST differ from `MEDIA_VOCAB_QUERY_KEY`: with the global 30 s `staleTime`
 * a reader keyed on the plain dictionary would hand the admin editor a cached
 * `Vocab` whose tags carry no `usageCount`, and every tag would show "0 kép"
 * plus a delete button (audit I3). Built as a prefix extension so a plain
 * `invalidateQueries({ queryKey: MEDIA_VOCAB_QUERY_KEY })` — TanStack's
 * default partial matching — still invalidates this one too.
 */
export const MEDIA_VOCAB_USAGE_QUERY_KEY = [...MEDIA_VOCAB_QUERY_KEY, 'usage'] as const;

/**
 * Base React Query key for a hotel's media list. `MediaLibraryBrowser`
 * appends `[filter, cursor]` to this for its own `useQuery` — every other
 * caller that changes media (bulk bar, detail dialog, upload panel
 * consumers) invalidates with exactly this prefix so TanStack Query's
 * default partial-key matching picks up every loaded filter/cursor
 * combination, without those callers needing to know the browser's full key
 * shape. Exported so nobody has to retype the `['hotel-media', hotelId]`
 * literal in a second place.
 */
export function mediaListQueryKey(hotelId: string): readonly [string, string] {
  return ['hotel-media', hotelId] as const;
}

export async function listVocab(withUsage?: boolean): Promise<Vocab> {
  const res = await authFetch(`/media-vocab${withUsage ? '?usage=1' : ''}`);
  return mediaJson<Vocab>(res, 'failed to list media vocabulary');
}

export async function createVocabTag(body: CreateVocabTagBody): Promise<VocabTag> {
  const res = await authFetch('/media-vocab/tags', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return mediaJson<VocabTag>(res, 'failed to create tag');
}

export async function patchVocabTag(
  id: string,
  body: PatchVocabTagBody,
): Promise<PatchVocabTagResult> {
  const res = await authFetch(`/media-vocab/tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return mediaJson<PatchVocabTagResult>(res, 'failed to update tag');
}

export async function deleteVocabTag(id: string): Promise<void> {
  const res = await authFetch(`/media-vocab/tags/${id}`, { method: 'DELETE' });
  await mediaJson<{ id: string }>(res, 'failed to delete tag');
}

export async function createVocabCategory(body: CreateVocabCategoryBody): Promise<VocabCategory> {
  const res = await authFetch('/media-vocab/categories', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return mediaJson<VocabCategory>(res, 'failed to create category');
}

export async function patchVocabCategory(
  id: string,
  body: PatchVocabCategoryBody,
): Promise<VocabCategory> {
  const res = await authFetch(`/media-vocab/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return mediaJson<VocabCategory>(res, 'failed to update category');
}

export type {
  AiInvolvement,
  BulkMediaBody,
  BulkMediaOp,
  BulkMediaResult,
  ComplianceStatus,
  CreateVocabCategoryBody,
  CreateVocabTagBody,
  HotelMedia,
  MediaApiError,
  MediaApiErrorFields,
  MediaComplianceFields,
  MediaComplianceFieldsPatch,
  MediaFilter,
  MediaListPage,
  MediaPatchBody,
  Orientation,
  PatchVocabCategoryBody,
  PatchVocabTagBody,
  PatchVocabTagResult,
  Vocab,
  VocabCategory,
  VocabTag,
} from './types.js';
